import React, { useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import DatePicker from "react-datepicker";
import { User, Calendar } from "lucide-react";
import { calculateAgeFromDOB, formatAgeDisplay } from "../HVTCommonHelpers";
// import { calculateAgeUnitFromDOB, calculateDOBFromAge } from "../HVTCommonHelpers";

const PREFERRED_GENDER_ORDER = ["Male", "Female", "Transgender", "NA", "Veterinary"];
const MOBILE_PATTERN = /^\d{10}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MAX_AGE = 150;
const today = new Date();
const MIN_DOB = new Date(today.getFullYear() - MAX_AGE, today.getMonth(), today.getDate());

const PatientDetailsSection = ({ salutationOptions }) => {
    const { register, control, setValue, watch, formState: { errors } } = useFormContext();
    const gender = watch("gender");
    const age = watch("age");
    const dobRef = useRef(null);
    // const [ageUnit, setAgeUnit] = useState("Year");

    // const AGE_UNIT_MAX = { Year: MAX_AGE, Month: 12, Week: 4, Day: 7 };

    // const applyAge = (rawVal, unit) => {
    //     let val = String(rawVal).split(".")[0].replace(/^0+(?=\d)/, "");
    //     if (parseInt(val, 10) > MAX_AGE) val = String(MAX_AGE);
    //     setValue("age", val, { shouldValidate: true });
    //     setValue("dob", val ? calculateDOBFromAge(val, unit) : null, { shouldValidate: true });
    // };

    const genderOptions = useMemo(() => {
        const fromApi = [...new Set(salutationOptions.map((s) => s.Gender).filter(Boolean))];
        if (!fromApi.length) return PREFERRED_GENDER_ORDER;
        return [
            ...PREFERRED_GENDER_ORDER.filter((g) => fromApi.includes(g)),
            ...fromApi.filter((g) => !PREFERRED_GENDER_ORDER.includes(g)),
        ];
    }, [salutationOptions]);

    return (
        <div className="hvt-quick-section">
            <div className="hvt-quick-section-title"><User size={13} /> Patient Details</div>

            <div className="hvt-field-grid">
                <div className="hvt-field">
                    <label className="required">Gender</label>
                    <select
                        className="form-select"
                        {...register("gender", { required: "Required" })}
                    >
                        <option value="">Select</option>
                        {genderOptions.map((g) => (
                            <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                    {errors.gender && <small className="text-danger">{errors.gender.message}</small>}
                </div>
                <div className="hvt-field">
                    <label className="required">Salutation</label>
                    <select
                        className="form-select"
                        disabled={!gender}
                        {...register("salutation", { required: "Required" })}
                    >
                        <option value="">Select</option>
                        {salutationOptions.filter((s) => s.Gender === gender).map((s) => (
                            <option key={s.Id} value={s.Salutation}>{s.Salutation}</option>
                        ))}
                    </select>
                    {errors.salutation && <small className="text-danger">{errors.salutation.message}</small>}
                </div>
            </div>

            <div className="hvt-field-grid mt-2">
                <div className="hvt-field">
                    <label className="required">First name</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="First name"
                        {...register("firstName", {
                            required: "Required",
                            onChange: (e) => {
                                const cleaned = e.target.value.replace(/[^A-Za-z ]/g, "");
                                setValue("firstName", cleaned.charAt(0).toUpperCase() + cleaned.slice(1), { shouldValidate: true });
                            },
                        })}
                    />
                    {errors.firstName && <small className="text-danger">{errors.firstName.message}</small>}
                </div>
                <div className="hvt-field">
                    <label className="required">Last name</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Last name"
                        {...register("lastName", {
                            required: "Required",
                            onChange: (e) => {
                                const cleaned = e.target.value.replace(/[^A-Za-z ]/g, "");
                                setValue("lastName", cleaned.charAt(0).toUpperCase() + cleaned.slice(1), { shouldValidate: true });
                            },
                        })}
                    />
                    {errors.lastName && <small className="text-danger">{errors.lastName.message}</small>}
                </div>
            </div>


            <div className="hvt-field-grid mt-2">
            <div className="hvt-field">
                    <label className="required">DOB</label>
                    <div className="position-relative">
                        <Controller
                            name="dob"
                            control={control}
                            rules={{ required: "Required" }}
                            render={({ field }) => (
                                <DatePicker
                                    ref={dobRef}
                                    portalId="hvt-datepicker-portal"
                                    className="form-control"
                                    selected={field.value}
                                    onChange={(date) => {
                                        if (!date) {
                                            field.onChange(date);
                                            setValue("age", "", { shouldValidate: true });
                                            return;
                                        }
                                        if (date > new Date()) date = new Date();
                                        if (date < MIN_DOB) date = MIN_DOB;
                                        field.onChange(date);
                                        // const { value, unit } = calculateAgeUnitFromDOB(date);
                                        // setAgeUnit(unit);
                                        // setValue("age", value, { shouldValidate: true });
                                        setValue("age", calculateAgeFromDOB(date), { shouldValidate: true });
                                    }}
                                    dateFormat={["dd/MM/yyyy", "ddMMyyyy", "dd-MM-yyyy"]}
                                    minDate={MIN_DOB}
                                    maxDate={new Date()}
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    placeholderText="DD/MM/YYYY"
                                />
                            )}
                        />
                        <Calendar
                            size={16}
                            style={{ position: "absolute", right: 12, top: 10, cursor: "pointer" }}
                            onClick={() => dobRef.current?.setFocus()}
                        />
                    </div>
                    {errors.dob && <small className="text-danger">{errors.dob.message}</small>}
                </div>
                <div className="hvt-field">
                    <label className="required">Age</label>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Age"
                            disabled
                            value={formatAgeDisplay(age)}
                        />
                        {/* <select
                            className="form-select"
                            style={{ flex: "0 0 55%" }}
                            value={ageUnit}
                            onChange={(e) => {
                                const newUnit = e.target.value;
                                setAgeUnit(newUnit);
                                setValue("age", "", { shouldValidate: true });
                                setValue("dob", null, { shouldValidate: true });
                            }}
                        >
                            <option value="Year">Year(s)</option>
                            <option value="Month">Month(s)</option>
                            <option value="Week">Week(s)</option>
                            <option value="Day">Day(s)</option>
                        </select> */}
                    </div>
                    <input type="hidden" {...register("age", { required: "Required" })} />
                    {errors.age && <small className="text-danger">{errors.age.message}</small>}
                </div>
            </div>

            <div className="hvt-field-grid mt-2">
                <div className="hvt-field">
                    <label className="required">Mobile</label>
                    <div className="input-group">
                        <span className="input-group-text">+91</span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Mobile number"
                            maxLength={10}
                            {...register("mobile", {
                                required: "Required",
                                pattern: { value: MOBILE_PATTERN, message: "Enter a valid 10-digit mobile number" },
                                onChange: (e) => setValue("mobile", e.target.value.replace(/\D/g, ""), { shouldValidate: true }),
                            })}
                        />
                    </div>
                    {errors.mobile && <small className="text-danger">{errors.mobile.message}</small>}
                </div>
                <div className="hvt-field">
                    <label className="required">Email</label>
                    <input
                        type="email"
                        className="form-control"
                        placeholder="Email"
                        {...register("email", {
                            required: "Required",
                            pattern: { value: EMAIL_PATTERN, message: "Enter a valid email" },
                            onChange: (e) => setValue("email", e.target.value.toLowerCase(), { shouldValidate: true }),
                        })}
                    />
                    {errors.email && <small className="text-danger">{errors.email.message}</small>}
                </div>
            </div>


        </div>
    );
};

export default PatientDetailsSection;
